import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslations } from "next-intl"
import {
	ArrowLeft, Brain, Building2, Calendar, FileText, ExternalLink, Tag,
	Banknote, Sparkles, Globe, Target,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/apiService"
import { formatCurrency, formatDate, getRelevanceBgColor } from "@/lib/utils"
import type { Grant } from "@/types/grant.types"

interface GrantDetailResponse {
	success: boolean
	data: Grant
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
	return (
		<div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
			<div className="text-[#2D8A5E] mt-0.5 shrink-0">{icon}</div>
			<div className="min-w-0 flex-1">
				<p className="text-xs text-muted-foreground font-medium mb-0.5">{label}</p>
				<div className="text-sm font-semibold text-gray-800 break-words">{value}</div>
			</div>
		</div>
	)
}

function RelevanceCard({ score, reason, t }: { score?: number | null; reason?: string | null; t: ReturnType<typeof useTranslations> }) {
	if (score == null) {
		return (
			<div className="bg-gray-50 rounded-xl border border-gray-100 p-5">
				<div className="flex items-center gap-2 mb-2">
					<Brain className="h-5 w-5 text-muted-foreground" />
					<h3 className="font-semibold text-gray-700">{t("grants.detail.aiAnalysis")}</h3>
				</div>
				<p className="text-sm text-muted-foreground">—</p>
			</div>
		)
	}

	const bgCls = getRelevanceBgColor(score)

	return (
		<div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
			<div className="flex items-center gap-2 mb-4">
				<Brain className="h-5 w-5 text-[#2D8A5E]" />
				<h3 className="font-semibold text-gray-800">{t("grants.detail.aiAnalysis")}</h3>
			</div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm text-muted-foreground">Score</span>
				<span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border ${bgCls}`}>
					<Brain className="h-3.5 w-3.5" />
					{score} / 100
				</span>
			</div>
			<div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
				<div
					className={`h-full rounded-full transition-all ${score >= 75 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-400"}`}
					style={{ width: `${score}%` }}
				/>
			</div>
			{reason && (
				<div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
					<p className="text-sm text-gray-700 leading-relaxed">{reason}</p>
				</div>
			)}
		</div>
	)
}

function ListSection({ title, items }: { title: string; items: { descripcion: string; codigo?: string }[] | undefined }) {
	if (!items || items.length === 0) return null
	return (
		<div className="bg-white rounded-xl border border-gray-100 p-5">
			<h3 className="font-semibold text-gray-800 mb-3 text-sm">{title}</h3>
			<div className="flex flex-wrap gap-2">
				{items.map((it, i) => (
					<span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
						{it.descripcion}
					</span>
				))}
			</div>
		</div>
	)
}

export default function GrantDetailPage() {
	const t = useTranslations()
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const [grant, setGrant] = useState<Grant | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		if (!id) return
		setIsLoading(true)
		api.get<GrantDetailResponse>(`/grants/${id}`)
			.then((res) => setGrant(res.data))
			.catch(() => setGrant(null))
			.finally(() => setIsLoading(false))
	}, [id])

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex justify-center py-20">
					<LoadingSpinner />
				</div>
			</DashboardLayout>
		)
	}

	if (!grant) {
		return (
			<DashboardLayout>
				<div className="text-center py-20">
					<p className="text-muted-foreground">404</p>
					<Button variant="outline" className="mt-4" onClick={() => navigate("/grants")}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						{t("grants.backToList")}
					</Button>
				</div>
			</DashboardLayout>
		)
	}

	const isOpen = grant.abierto === true ||
		(grant.fecha_fin_solicitud && new Date(grant.fecha_fin_solicitud) >= new Date())

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-4">
					<div className="flex-1 min-w-0">
						<Button variant="ghost" size="sm" onClick={() => navigate("/grants")} className="mb-3 -ml-2">
							<ArrowLeft className="h-4 w-4 mr-1" />
							{t("grants.backToList")}
						</Button>
						<div className="flex items-center gap-2 mb-2 flex-wrap">
							<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono text-muted-foreground bg-gray-100 border border-gray-200">
								BDNS {grant.codigo_bdns}
							</span>
							{isOpen ? (
								<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-700 border-green-200">
									{t("grants.badges.open")}
								</span>
							) : (
								<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">
									{t("grants.badges.closed")}
								</span>
							)}
							{grant.mrr && (
								<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
									<Sparkles className="h-3 w-3 mr-1" />
									{t("grants.badges.mrr")}
								</span>
							)}
						</div>
						<h1 className="text-2xl font-semibold text-gray-900 leading-tight">
							{grant.descripcion || "—"}
						</h1>
						{grant.descripcion_leng && grant.descripcion_leng !== grant.descripcion && (
							<p className="text-sm text-muted-foreground mt-1">{grant.descripcion_leng}</p>
						)}
					</div>
					{grant.source_url && (
						<Button variant="outline" size="sm" asChild>
							<a href={grant.source_url} target="_blank" rel="noreferrer">
								<ExternalLink className="h-4 w-4 mr-2" />
								{t("grants.detail.sourceBdns")}
							</a>
						</Button>
					)}
				</div>

				{/* Info grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
					<InfoCard
						icon={<Building2 className="h-5 w-5" />}
						label={t("grants.detail.organismo")}
						value={
							<>
								<p>{grant.nivel3 || "—"}</p>
								<p className="text-xs text-muted-foreground font-normal mt-0.5">{[grant.nivel1, grant.nivel2].filter(Boolean).join(" › ")}</p>
							</>
						}
					/>
					<InfoCard
						icon={<Banknote className="h-5 w-5" />}
						label={t("grants.detail.budget")}
						value={formatCurrency(grant.presupuesto_total) || "—"}
					/>
					<InfoCard
						icon={<Tag className="h-5 w-5" />}
						label="Tipo"
						value={grant.tipo_convocatoria || "—"}
					/>
					<InfoCard
						icon={<Calendar className="h-5 w-5" />}
						label={t("grants.detail.recepcion")}
						value={formatDate(grant.fecha_recepcion) || "—"}
					/>
					<InfoCard
						icon={<Calendar className="h-5 w-5" />}
						label={t("grants.detail.plazoInicio")}
						value={
							grant.fecha_inicio_solicitud
								? formatDate(grant.fecha_inicio_solicitud)
								: grant.text_inicio || "—"
						}
					/>
					<InfoCard
						icon={<Calendar className="h-5 w-5" />}
						label={t("grants.detail.plazoFin")}
						value={
							grant.fecha_fin_solicitud
								? formatDate(grant.fecha_fin_solicitud)
								: grant.text_fin || "—"
						}
					/>
					{grant.descripcion_finalidad && (
						<InfoCard
							icon={<Target className="h-5 w-5" />}
							label={t("grants.detail.finalidad")}
							value={grant.descripcion_finalidad}
						/>
					)}
					{grant.sede_electronica && (
						<InfoCard
							icon={<Globe className="h-5 w-5" />}
							label={t("grants.detail.sedeElectronica")}
							value={
								<a href={grant.sede_electronica} target="_blank" rel="noreferrer" className="text-[#2D8A5E] hover:underline break-all">
									{grant.sede_electronica}
								</a>
							}
						/>
					)}
					{grant.url_bases_reguladoras && (
						<InfoCard
							icon={<FileText className="h-5 w-5" />}
							label={t("grants.detail.bases")}
							value={
								<a href={grant.url_bases_reguladoras} target="_blank" rel="noreferrer" className="text-[#2D8A5E] hover:underline">
									{t("grants.detail.basesUrl")} ↗
								</a>
							}
						/>
					)}
				</div>

				{/* AI relevance */}
				<RelevanceCard score={grant.ai_relevance_score} reason={grant.ai_relevance_reason} t={t} />

				{/* Listas */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<ListSection title={t("grants.detail.beneficiarios")} items={grant.tipos_beneficiarios} />
					<ListSection title={t("grants.detail.sectores")} items={grant.sectores} />
					<ListSection title={t("grants.detail.regiones")} items={grant.regiones} />
					<ListSection title={t("grants.detail.instrumentos")} items={grant.instrumentos} />
				</div>

				{/* Bases reguladoras descripción */}
				{grant.descripcion_bases_reguladoras && (
					<div className="bg-white rounded-xl border border-gray-100 p-5">
						<div className="flex items-center gap-2 mb-3">
							<FileText className="h-4 w-4 text-[#2D8A5E]" />
							<h3 className="font-semibold text-gray-800 text-sm">{t("grants.detail.bases")}</h3>
						</div>
						<p className="text-sm text-gray-700 leading-relaxed">{grant.descripcion_bases_reguladoras}</p>
					</div>
				)}
			</div>
		</DashboardLayout>
	)
}
